/**
 * App router.
 *
 * Route map:
 *   /                      → redirect to /welcome (which itself bounces to /login if unauthenticated)
 *   /login                 → PublicRoute → LoginPage
 *   /otp                   → PublicRoute → OtpPage  (requires phone in router state; else redirects to /login)
 *   /welcome               → ProtectedRoute → PostLoginPage (post-login chooser: enter a clinic vs personal panel)
 *   /clinic-entry          → ProtectedRoute → ClinicEntryPage (type/scan a 6-digit clinic code)
 *   /dashboard             → ProtectedRoute → DashboardPage
 *   /signup                → ProtectedRoute → SignupPage (post-OTP onboarding for new users)
 *   /profile               → ProtectedRoute → ProfilePage
 *   /services              → ProtectedRoute → ServicesPage
 *   /cart                  → ProtectedRoute → CartPage (review + hand off to landing payment)
 *   /services/:serviceId/checkout → ProtectedRoute → CheckoutRedirect (legacy deep-link → adds to cart, /cart)
 *   /services/:serviceId   → ProtectedRoute → ServiceDetailPage
 *   /checkout/return       → ProtectedRoute → CheckoutReturnPage
 *   /transactions          → ProtectedRoute → TransactionsPage
 *   /service-history       → ProtectedRoute → ServiceHistoryPage
 *   /support               → ProtectedRoute → SupportPage (list)
 *   /support/new           → ProtectedRoute → NewTicketPage
 *   /support/:ticketId     → ProtectedRoute → TicketDetailPage
 *   /clinic-tickets            → ProtectedRoute → ClinicTicketsPage (list)
 *   /clinic-tickets/new        → ProtectedRoute → NewClinicTicketPage
 *   /clinic-tickets/:ticketId  → ProtectedRoute → ClinicTicketDetailPage
 *   *                      → NotFound
 *
 * Guards:
 *   - ProtectedRoute redirects unauthenticated users to /login, AND
 *     redirects authenticated-but-no-profile users to /signup
 *   - PublicRoute redirects authenticated users to /welcome (with-profile)
 *     or /signup (without-profile) so the OTP race never lands a new
 *     user on /welcome
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ClientQuickBar from '@/components/ClientQuickBar';
import ConsultationsPage from '@/pages/ConsultationsPage';
import CandleCardPage from '@/pages/CandleCardPage';
import VisualReportPage from '@/pages/VisualReportPage';
import ConsultationDetailPage from '@/pages/ConsultationDetailPage';
import RerunPayPage from '@/pages/RerunPayPage';
import PickPhysicianPage from '@/pages/PickPhysicianPage';
import StartConsultationPage from '@/pages/StartConsultationPage';
import PublicStartPage from '@/pages/PublicStartPage';
import ConsultationPreviewPage from '@/pages/ConsultationPreviewPage';
import ChooseServicePage from '@/pages/ChooseServicePage';
import HeaderBar from '@/components/HeaderBar';

import NotFound from '@/pages/NotFound';
import LoginPage from '@/pages/LoginPage';
import OtpPage from '@/pages/OtpPage';
import ClinicEntryPage from '@/pages/ClinicEntryPage';
import DashboardPage from '@/pages/DashboardPage';
import NotesPage from '@/pages/NotesPage';
import SignupPage from '@/pages/SignupPage';
import ProfilePage from '@/pages/ProfilePage';
import ServicesPage from '@/pages/ServicesPage';
import ServiceDetailPage from '@/pages/ServiceDetailPage';
import CheckoutRedirect from '@/pages/CheckoutRedirect';
import CartPage from '@/pages/CartPage';
import CheckoutReturnPage from '@/pages/CheckoutReturnPage';
import TransactionsPage from '@/pages/TransactionsPage';
import ServiceHistoryPage from '@/pages/ServiceHistoryPage';
import ReportPage from '@/pages/ReportPage';
import CreditsPage from '@/pages/CreditsPage';
import SharingPage from '@/pages/SharingPage';
import FaceCapturePage from '@/pages/FaceCapturePage';
import SupportPage from '@/pages/SupportPage';
import NewTicketPage from '@/pages/NewTicketPage';
import TicketDetailPage from '@/pages/TicketDetailPage';
import ClinicTicketsPage from '@/pages/ClinicTicketsPage';
import NewClinicTicketPage from '@/pages/NewClinicTicketPage';
import ClinicTicketDetailPage from '@/pages/ClinicTicketDetailPage';
import ProtectedRoute from '@/routes/ProtectedRoute';
import PublicRoute from '@/routes/PublicRoute';

export function AppRouter() {
  return (
    <BrowserRouter>
      <HeaderBar />
      <Routes>
        {/* Root → welcome chooser (which bounces to /login if not signed in) */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Public pages — redirect away if already authenticated */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/otp"
          element={
            <PublicRoute>
              <OtpPage />
            </PublicRoute>
          }
        />

        {/* Protected pages — require authentication */}
        {/* Post-login chooser removed — go straight to the personal dashboard. */}
        <Route path="/welcome" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/clinic-entry"
          element={
            <ProtectedRoute>
              <ClinicEntryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <ProtectedRoute>
              <SignupPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notes"
          element={
            <ProtectedRoute>
              <NotesPage />
            </ProtectedRoute>
          }
        />
        <Route path="/candle-card" element={<ProtectedRoute><CandleCardPage /></ProtectedRoute>} />
        {/* One report, drawn on the client's own face. Registered after the
            section itself so '/candle-card' is not shadowed. */}
        <Route path="/candle-card/report/:consultationId" element={<ProtectedRoute><VisualReportPage /></ProtectedRoute>} />
        <Route
          path="/consultations"
          element={
            <ProtectedRoute>
              <ConsultationsPage />
            </ProtectedRoute>
          }
        />
        {/* PUBLIC: the landing page's call to action lands here. No auth guard —
            the visitor signs in inline, on this page, once they have been told
            what they are getting. */}
        <Route path="/start" element={<PublicStartPage />} />
        {/* Free preview after capture: detected points + how agents read them. */}
        <Route
          path="/consultations/:consultationId/preview"
          element={
            <ProtectedRoute>
              <ConsultationPreviewPage />
            </ProtectedRoute>
          }
        />
        {/* Then: which analysis, then which doctor, then pay. */}
        <Route
          path="/consultations/:consultationId/choose-service"
          element={
            <ProtectedRoute>
              <ChooseServicePage />
            </ProtectedRoute>
          }
        />
        {/* Entry point for the capture-first consultation flow. */}
        <Route
          path="/start-consultation"
          element={
            <ProtectedRoute>
              <StartConsultationPage />
            </ProtectedRoute>
          }
        />
        {/* RULE 4 — the client picks their doctor AFTER paying. This screen
            is what issues the face-capture link, so it gates the rest of
            the flow. */}
        <Route
          path="/consultations/:consultationId/choose-physician"
          element={
            <ProtectedRoute>
              <PickPhysicianPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/consultations/:consultationId"
          element={
            <ProtectedRoute>
              <ConsultationDetailPage />
            </ProtectedRoute>
          }
        />
        {/* Support texts this link when a report has to be re-run and the
            client is the one paying for the extra attempt. Protected: the
            re-run belongs to one client, so the server checks the session
            matches before it shows an amount. */}
        <Route
          path="/rerun/:rerunId"
          element={
            <ProtectedRoute>
              <RerunPayPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/services"
          element={
            <ProtectedRoute>
              <ServicesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cart"
          element={
            <ProtectedRoute>
              <CartPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/services/:serviceId/checkout"
          element={
            <ProtectedRoute>
              <CheckoutRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/services/:serviceId"
          element={
            <ProtectedRoute>
              <ServiceDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout/return"
          element={
            <ProtectedRoute>
              <CheckoutReturnPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/transactions"
          element={
            <ProtectedRoute>
              <TransactionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-history"
          element={
            <ProtectedRoute>
              <ServiceHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/report/:analysisId"
          element={
            <ProtectedRoute>
              <ReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/credits"
          element={
            <ProtectedRoute>
              <CreditsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sharing"
          element={
            <ProtectedRoute>
              <SharingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/face-capture"
          element={
            <ProtectedRoute>
              <FaceCapturePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <SupportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support/new"
          element={
            <ProtectedRoute>
              <NewTicketPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support/:ticketId"
          element={
            <ProtectedRoute>
              <TicketDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Clinic tickets (customer ↔ clinic) — separate stream from support.
            /clinic-tickets/new is registered BEFORE /:ticketId so the literal
            "new" doesn't match the param route. */}
        <Route
          path="/clinic-tickets"
          element={
            <ProtectedRoute>
              <ClinicTicketsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clinic-tickets/new"
          element={
            <ProtectedRoute>
              <NewClinicTicketPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clinic-tickets/:ticketId"
          element={
            <ProtectedRoute>
              <ClinicTicketDetailPage />
            </ProtectedRoute>
          }
        />

        {/* 404 fallback — must be last */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <ClientQuickBar />
    </BrowserRouter>
  );
}

export default AppRouter;
